import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/src/lib/components/custom/Icon";
import { Button } from "@/src/lib/components/ui/button";
import { Card } from "@/src/lib/components/ui/card";
import { Input } from "@/src/lib/components/ui/input";
import { Label } from "@/src/lib/components/ui/label";
import { useApi } from "@/src/lib/hooks/api/use-api";
import type { GetTypes } from "@/src/lib/types/api-types";

interface AccountInfoProps {
	session: GetTypes["auth"]["useSession"] | null;
	userProfile?: GetTypes["users"]["getCurrentUserProfile"];
	onSessionUpdate: () => void;
}

export function AccountInfo({ session, userProfile }: AccountInfoProps) {
	const { t } = useTranslation();
	const { auth, users } = useApi();

	// Refs for input fields
	const nameInputRef = useRef<HTMLInputElement>(null);
	const phoneInputRef = useRef<HTMLInputElement>(null);

	// Form state - separate editing states for name and phone
	const [isEditingName, setIsEditingName] = useState(false);
	const [isEditingPhone, setIsEditingPhone] = useState(false);
	const [formData, setFormData] = useState<{
		name?: string;
		phone?: string;
	}>({
		name: session?.user?.name || "",
		phone: userProfile?.user?.phone || "",
	});

	const handleFormChange = (field: "name" | "phone", value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleEditName = () => {
		setFormData((prev) => ({ ...prev, name: session?.user?.name || "" }));
		setIsEditingName(true);
		// Focus the input field after state update
		setTimeout(() => {
			nameInputRef.current?.focus();
		}, 0);
	};

	const handleCancelName = () => {
		setFormData((prev) => ({ ...prev, name: session?.user?.name || "" }));
		setIsEditingName(false);
	};

	const handleSaveName = async () => {
		if (
			!session?.user?.id ||
			!formData.name ||
			formData.name === session.user.name
		)
			return;

		try {
			await auth.updateUser({ name: formData.name });
			setIsEditingName(false);
		} catch (error) {
			console.error("Failed to update name:", error);
		}
	};

	const handleEditPhone = () => {
		setFormData((prev) => ({ ...prev, phone: userProfile?.user?.phone || "" }));
		setIsEditingPhone(true);
		// Focus the input field after state update
		setTimeout(() => {
			phoneInputRef.current?.focus();
		}, 0);
	};

	const handleCancelPhone = () => {
		setFormData((prev) => ({ ...prev, phone: userProfile?.user?.phone || "" }));
		setIsEditingPhone(false);
	};

	const handleSavePhone = () => {
		if (!session?.user?.id) return;

		const phoneToSave = formData.phone || "";

		users.updateProfile.mutate(
			{ phone: phoneToSave },
			{
				onSuccess: () => {
					// Update the form data to ensure the UI shows the saved value immediately
					setFormData((prev) => ({ ...prev, phone: phoneToSave }));
					setIsEditingPhone(false);
					// Session and profile are automatically refreshed by the mutation
				},
				onError: (error) => {
					console.error("Failed to update phone:", error);
				},
			},
		);
	};

	return (
		<Card className="p-6">
			<div className="mb-6">
				<h3 className="text-xl font-semibold">
					{t("profile.account_info.header")}
				</h3>
			</div>

			<div className="space-y-6">
				{/* Name Section */}
				<div className="flex items-end gap-3">
					<div className="flex-1">
						<Label htmlFor="name">{t("profile.account_info.name_label")}</Label>
						<Input
							ref={nameInputRef}
							id="name"
							value={
								isEditingName ? formData.name || "" : session?.user?.name || ""
							}
							onChange={(e) => handleFormChange("name", e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && isEditingName) {
									handleSaveName();
								} else if (e.key === "Escape" && isEditingName) {
									handleCancelName();
								}
							}}
							disabled={!isEditingName}
							className="mt-1"
						/>
					</div>
					<div className="flex gap-2 pb-1">
						{!isEditingName ? (
							<Button variant="outline" size="sm" onClick={handleEditName}>
								<Icon name="Pencil" className="w-4 h-4" />
							</Button>
						) : (
							<>
								<Button variant="outline" size="sm" onClick={handleCancelName}>
									<Icon name="X" className="w-4 h-4" />
								</Button>
								<Button
									variant="primary"
									size="sm"
									onClick={handleSaveName}
									disabled={false} // auth.updateUser doesn't have loading state
								>
									<Icon name="Check" className="w-4 h-4" />
								</Button>
							</>
						)}
					</div>
				</div>

				{/* Phone Section */}
				<div className="flex items-end gap-3">
					<div className="flex-1">
						<Label htmlFor="phone">
							{t("profile.account_info.phone_label")}
						</Label>
						<Input
							ref={phoneInputRef}
							id="phone"
							value={
								isEditingPhone
									? formData.phone || ""
									: userProfile?.user?.phone || ""
							}
							onChange={(e) => handleFormChange("phone", e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && isEditingPhone) {
									handleSavePhone();
								} else if (e.key === "Escape" && isEditingPhone) {
									handleCancelPhone();
								}
							}}
							disabled={!isEditingPhone || users.updateProfile.isPending}
							placeholder={t("profile.account_info.phone_placeholder")}
							className="mt-1"
						/>
					</div>
					<div className="flex gap-2 pb-1">
						{!isEditingPhone ? (
							<Button variant="outline" size="sm" onClick={handleEditPhone}>
								<Icon name="Pencil" className="w-4 h-4" />
							</Button>
						) : (
							<>
								<Button
									variant="outline"
									size="sm"
									onClick={handleCancelPhone}
									disabled={users.updateProfile.isPending}
								>
									<Icon name="X" className="w-4 h-4" />
								</Button>
								<Button
									variant="primary"
									size="sm"
									onClick={handleSavePhone}
									disabled={users.updateProfile.isPending}
								>
									{users.updateProfile.isPending ? (
										<Icon
											name="LoaderPinwheel"
											className="w-4 h-4 animate-spin"
										/>
									) : (
										<Icon name="Check" className="w-4 h-4" />
									)}
								</Button>
							</>
						)}
					</div>
				</div>

				<div className="pt-4 border-t space-y-4">
					<div className="flex items-center justify-between">
						<div>
							<Label className="text-base">
								{t("profile.account_info.addresses.label")}
							</Label>
							<p className="text-sm text-muted-foreground">
								{t("profile.account_info.addresses.subheader")}
							</p>
						</div>
						<Link to="/profile/addresses">
							<Button variant="outline" size="sm">
								<Icon name="MapPin" className="w-4 h-4 mr-2" />
								{t("profile.account_info.addresses.manage")}
							</Button>
						</Link>
					</div>
				</div>
			</div>
		</Card>
	);
}
